import React, { useState, useEffect, useMemo } from 'react';
import { Briefcase, MapPin, IndianRupee, Clock, PhoneCall, Search, Building, Users, AlertTriangle, RotateCw, RefreshCw } from 'lucide-react';

const API_BASE = 'https://mukadampro.shirpur.store';

// --- INJECT CUSTOM ANIMATION STYLES FOR PERFORMANCE & LUXURY UI ---
const styleTag = document.createElement("style");
styleTag.innerHTML = `
/* 🚀 PERFORMANCE, SMOOTHNESS & PREMIUM SCROLLBAR CSS 🚀 */
* { -webkit-tap-highlight-color: transparent; scroll-behavior: smooth; }
body { 
   -webkit-font-smoothing: antialiased; 
   -moz-osx-font-smoothing: grayscale; 
   overscroll-behavior-y: none; 
   zoom: 0.95 !important;
   font-size: 14px;
}

/* 🌟 NAYA: Refresh Icon ke smooth ghoomne ke liye animation rule */
@keyframes premium-spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.luxury-glass-hub {
    background: rgba(255, 255, 255, 0.72) !important;
    backdrop-filter: blur(30px) saturate(200%) !important;
    -webkit-backdrop-filter: blur(30px) saturate(200%) !important;
    border: 1px solid rgba(255, 255, 255, 0.6) !important;
    box-shadow: 0 20px 48px rgba(15, 23, 42, 0.05), inset 0 1px 1px rgba(255, 255, 255, 1) !important;
    border-radius: 32px !important;
}

.interactive-search-bar {
    border: 1px solid #E2E8F0 !important;
    transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1) !important;
}
.interactive-search-bar:focus-within {
    border-color: #0F172A !important;
    box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08), 0 4px 12px rgba(15, 23, 42, 0.02) !important;
    background: #FFFFFF !important;
}

.micro-click-pill {
    transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1) !important;
}
.micro-click-pill:active {
    transform: scale(0.94) !important;
}

.ultra-clean-scroll::-webkit-scrollbar {
    display: none !important;
}
.ultra-clean-scroll {
    -ms-overflow-style: none !important;
    scrollbar-width: none !important;
}

.force-white { color: white !important; }
.force-green { color: #10B981 !important; }
.force-blue-text { color: #2563EB !important; }
.force-grey { color: #64748B !important; }
.force-red { color: #ed2222ff !important; }
`;
document.head.appendChild(styleTag);

export default function VacanciesView({ appBg, handleVoiceClick }) {
    const [vacancies, setVacancies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('All'); 
    const [errorMsg, setErrorMsg] = useState(null);

    // 🌟 PULL TO REFRESH STATES FOR MOBILE/TOUCH SCROLL-UP
    const [startY, setStartY] = useState(0);
    const [pullOffset, setPullOffset] = useState(0);
    const [isPulling, setIsPulling] = useState(false);

    const appBgFinal = appBg || localStorage.getItem('appBg') || "url('/OHOME.jpg') center / cover no-repeat fixed";
    const categories = ['All', 'Loom Worker', 'Loom Mukadam', 'Rach Worker', 'Metha Worker', 'Taration Worker', 'Sweeper', 'Other'];

    const getHindiCatName = (cat) => {
        const cleanName = cat.trim();
        const hindiMap = {
            'All': 'ऑल',
            'Loom Worker': 'लूम वर्कर',
            'Loom Mukadam': 'लूम मुकादम',
            'Rach Worker': 'राछ वर्कर',
            'Metha Worker': 'मेठा वर्कर',
            'Taration Worker': 'टारेशन वर्कर',
            'Sweeper': 'स्वीपर',
            'Other': 'अन्य'
        };
        return hindiMap[cleanName] || cleanName;
    };

    useEffect(() => {
        fetchVacancies();
    }, []);

    const fetchVacancies = async () => {
        try {
            setLoading(true);
            setErrorMsg(null);
            const res = await fetch(`${API_BASE}/get_explore_jobs.php?action=get_all_open`);
            const rawText = await res.text();
            
            try {
                const json = JSON.parse(rawText);
                if (json.status === 'success') {
                    setVacancies(json.data || []);
                } else {
                    setErrorMsg("Backend Error: " + (json.message || "Unknown error"));
                    setVacancies([]);
                }
            } catch (parseErr) {
                setErrorMsg("API format galat hai ya PHP Error aa raha hai.");
                setVacancies([]);
            }
        } catch (error) {
            setErrorMsg("Network/CORS Error: " + error.message);
            setVacancies([]);
        } finally {
            setLoading(false);
        }
    };

    // --- PULL-TO-REFRESH TOUCH TOUCHPAD HANDLERS ---
    const handleTouchStart = (e) => {
        if (window.scrollY === 0) {
            setStartY(e.touches[0].clientY);
            setIsPulling(true);
        }
    };

    const handleTouchMove = (e) => {
        if (!isPulling || window.scrollY > 0) return;
        const currentY = e.touches[0].clientY;
        const diff = currentY - startY;
        if (diff > 0) {
            // Rubber-band resistance applied (diff * 0.4) maxing out at 65px
            setPullOffset(Math.min(65, diff * 0.4));
        }
    };

    const handleTouchEnd = () => {
        if (!isPulling) return;
        setIsPulling(false);
        if (pullOffset >= 50) {
            fetchVacancies();
        }
        setPullOffset(0);
    };

    const filteredVacancies = useMemo(() => {
        return vacancies.filter(v => {
            const searchStr = searchTerm.toLowerCase().trim();
            const role = (v.role || '').toLowerCase().trim();
            const factory = (v.factory_name || '').toLowerCase().trim();
            const loc = (v.location || '').toLowerCase().trim();

            const matchesSearch = searchStr === '' || 
                role.includes(searchStr) || 
                factory.includes(searchStr) || 
                loc.includes(searchStr);
            
            const filterStr = activeFilter.toLowerCase().trim();
            const matchesTab = filterStr === 'all' || role === filterStr;
            
            return matchesSearch && matchesTab;
        });
    }, [vacancies, searchTerm, activeFilter]);

    return (
        <div 
            style={{...styles.content, background: appBgFinal}}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <style>{`
                * { font-family: 'Plus Jakarta Sans', sans-serif; box-sizing: border-box; }
            `}</style>

            {/* 🌟 LUXURY GLASS PULL-TO-REFRESH INDICATOR ENGINE 🌟 */}
            <div style={{
                height: pullOffset > 0 ? `${pullOffset}px` : '0px',
                opacity: pullOffset > 0 ? pullOffset / 50 : 0,
                transition: isPulling ? 'none' : 'height 0.3s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.3s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                gap: '8px',
                color: '#0F172A',
                fontWeight: '900',
                fontSize: '12px',
                letterSpacing: '0.5px'
            }}>
                <RefreshCw 
                    size={14} 
                    color="#0F172A" 
                    style={{ 
                        animation: pullOffset >= 50 || loading ? 'premium-spin 1s linear infinite' : 'none',
                        transform: `rotate(${pullOffset * 5}deg)` 
                    }} 
                />
                <span>{pullOffset >= 50 ? "रिलीज़ करके रिफ्रेश करें..." : "और नीचे खींचें..."}</span>
            </div>
            
            {/* ===================================================
                🌟 EXPLORE JOBS UNIFIED GLASS HUB 🌟
               =================================================== */}
            <div className="luxury-glass-hub" style={{ 
                padding: '18px', 
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                position: 'relative',
                overflow: 'hidden',
                marginBottom: '16px'
            }}>
                {/* Elegant Atmospheric Light Diffusion Blobs */}
                <div style={{ position: 'absolute', top: '-50px', left: '-50px', width: '140px', height: '140px', background: 'radial-gradient(circle, #38BDF8 0%, transparent 75%)', opacity: 0.15, filter: 'blur(28px)', pointerEvents: 'none' }} />
                <div style={{ position: 'absolute', bottom: '-50px', right: '-50px', width: '140px', height: '140px', background: 'radial-gradient(circle, #34D399 0%, transparent 75%)', opacity: 0.15, filter: 'blur(28px)', pointerEvents: 'none' }} />

               {/* ========================================================
                    🌟 FIXED: SEARCH BAR AND REFRESH ENGINE CONNECTED STRIP 🌟
                   ======================================================== */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', zIndex: 1 }}>
                    <div className="interactive-search-bar" style={{
                        display: 'flex', 
                        alignItems: 'center', 
                        background: 'rgba(248, 250, 252, 0.6)', 
                        borderRadius: '16px', 
                        padding: '12px 16px',
                        flex: 1 
                    }}>
                        <Search size={18} color="#475569" style={{ flexShrink: 0, opacity: 0.8 }} />
                        <input 
                            type="text" 
                            onClick={(e) => handleVoiceClick && handleVoiceClick(e, " यहाँ से आप जॉब का नाम या लोकेशन के हिसाब से जॉब सर्च कर सकते हैं।", null)} 
                            placeholder="Search role, area or location..." 
                            style={{
                                border: 'none', 
                                background: 'transparent', 
                                outline: 'none', 
                                width: '100%', 
                                marginLeft: '12px', 
                                fontSize: '13.5px', 
                                color: '#0F172A', 
                                fontWeight: '700',
                                fontFamily: "'Plus Jakarta Sans', sans-serif"
                            }} 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {/* 🌟 UPDATED: MODERN CHROME REFRESH ICON ENGINE 🌟 */}
                    <button 
                        onClick={(e) => handleVoiceClick && handleVoiceClick(e, " इस रिफ्रेश बटन से आप कारखानों की नई जॉब्स को दोबारा लोड कर सकते हैं।", () => fetchVacancies(), 'btn-refresh-jobs')}
                        className="micro-click-pill"
                        style={{
                            background: '#0F172A',
                            border: 'none',
                            borderRadius: '16px',
                            padding: '14px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                            flexShrink: 0
                        }}
                    >
                        <RefreshCw 
                            size={16} 
                            color="#FFFFFF" 
                            style={{ 
                                animation: loading ? 'premium-spin 1s linear infinite' : 'none' 
                            }}
                        />
                    </button>
                </div>
                
                {/* 2. HIGH-FURNISHED HORIZONTAL FILTER ENGINE */}
                <div className="ultra-clean-scroll" style={{ 
                    display: 'flex', 
                    gap: '10px', 
                    overflowX: 'auto', 
                    whiteSpace: 'nowrap', 
                    paddingBottom: '2px', 
                    margin: '0 -4px',
                    WebkitOverflowScrolling: 'touch',
                    zIndex: 1
                }}>
                    {categories.map((cat) => {
                        const hindiCat = getHindiCatName(cat); 
                        const isSelected = activeFilter === cat;

                        const voiceMessage = cat.toLowerCase() === 'all' 
                            ? " यह ऑल का बटन है, इसपर क्लिक करने से आपको सभी तरह की जॉब्स एक साथ दिखेंगी।"
                            : ` यह ${hindiCat} की जॉब्स देखने का फ़िल्टर है, इसपर क्लिक करने से सिर्फ ${hindiCat} की वेकेंसी दिखेंगी।`;

                        const alreadySelectedMessage = cat.toLowerCase() === 'all'
                            ? " आपने पहले से ही ऑल चुना हुआ है, अभी स्क्रीन पर आपको सभी तरह की जॉब्स एक साथ दिख रही हैं।"
                            : ` आपने पहले से ही ${hindiCat} का फ़िल्टर चुना हुआ है, अभी स्क्रीन पर आपको नीचे सिर्फ ${hindiCat} की ही जॉब्स दिख रही हैं।`;

                        const safeCatForCount = cat.trim();
                        const activeJobCount = cat.toLowerCase() === 'all' 
                            ? vacancies.length 
                            : vacancies.filter(job => String(job.role).trim().toLowerCase() === safeCatForCount.toLowerCase()).length;

                        return (
                            <button 
                                key={cat} 
                                className="micro-click-pill"
                                onClick={(e) => handleVoiceClick && handleVoiceClick(
                                    e, 
                                    voiceMessage, 
                                    () => setActiveFilter(cat), 
                                    `cat-${cat.trim()}`,
                                    isSelected,
                                    alreadySelectedMessage
                                )} 
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    padding: '10px 18px',
                                    borderRadius: '100px',
                                    fontSize: '12px',
                                    fontWeight: '800',
                                    cursor: 'pointer',
                                    flexShrink: 0,
                                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                                    position: cat.toLowerCase() === 'all' ? 'sticky' : 'static',
                                    left: cat.toLowerCase() === 'all' ? '0px' : 'auto',
                                    zIndex: cat.toLowerCase() === 'all' ? 20 : 1,
                                    background: isSelected 
                                        ? (cat.toLowerCase() === 'all' ? 'linear-gradient(135deg, #0284C7, #38BDF8)' : '#0F172A') 
                                        : '#FFFFFF',
                                    color: isSelected ? '#FFFFFF' : '#475569',
                                    border: isSelected ? 'none' : '1px solid #E2E8F0',
                                    boxShadow: isSelected 
                                        ? '0 8px 20px rgba(15, 23, 42, 0.16)' 
                                        : '0 2px 6px rgba(0,0,0,0.01)'
                                }}
                            >
                                {cat}
                                <span 
                                    style={{
                                        background: activeJobCount > 0 
                                            ? (isSelected ? 'rgba(255,255,255,0.25)' : '#10B981') 
                                            : (isSelected ? 'rgba(255,255,255,0.15)' : '#F1F5F9'), 
                                        color: activeJobCount > 0 || isSelected ? '#FFFFFF' : '#64748B',     
                                        padding: '3px 8px',
                                        borderRadius: '8px',
                                        fontSize: '9px',
                                        fontWeight: '900',
                                        boxShadow: activeJobCount > 0 && !isSelected ? '0 2px 6px rgba(16, 185, 129, 0.3)' : 'none',
                                        display: 'inline-block',
                                        lineHeight: '1'
                                    }}
                                >
                                    {activeJobCount}
                                </span>
                            </button>
                        )
                    })}
                </div>

                {/* 3. PREMIUM FINTECH STYLE DATA TICKER STRIP */}
                {!loading && filteredVacancies.length > 0 && (
                    <div style={{
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center', 
                        padding: '12px 16px', 
                        background: 'rgba(15, 23, 42, 0.03)', 
                        borderRadius: '14px', 
                        border: '1px solid rgba(15, 23, 42, 0.02)',
                        zIndex: 1
                    }}>
                        <span style={{ fontSize: '11px', fontWeight: '900', letterSpacing: '0.8px', textTransform: 'uppercase', color: '#334155' }}>
                            {filteredVacancies.length} {filteredVacancies.length === 1 ? 'Vacancy' : 'Vacancies'} Live
                        </span>
                        <span style={{ fontSize: '11px', color: '#059669', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '900', letterSpacing: '0.5px' }}>
                            <div style={{
                                width: '6px', 
                                height: '6px', 
                                borderRadius: '50%', 
                                background: '#10B981', 
                                boxShadow: '0 0 10px #10B981',
                                animation: 'pulseGlow 2s infinite'
                            }} /> 
                            VERIFIED NOW
                        </span>
                    </div>
                )}
            </div>

            {errorMsg && (
                <div style={{ background: 'rgba(239, 68, 68, 0.2)', backdropFilter: 'blur(10px)', border: '1px solid rgba(239, 68, 68, 0.3)', padding: '12px', borderRadius: '16px', marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                    <AlertTriangle size={18} color="#B91C1C" style={{flexShrink: 0}} />
                    <span style={{ fontSize: '12px', color: '#B91C1C', fontWeight: '600' }}>{errorMsg}</span>
                </div>
            )}

            {/* ===================================================
                🌟 ULTRA-LUXURY JOB BOX ENGINE 🌟
               =================================================== */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', color: '#111', fontWeight: '600', fontSize: '13px' }}>
                    Loading active jobs...
                </div>
            ) : filteredVacancies.length === 0 && !errorMsg ? (
                <div style={{ textAlign: 'center', padding: '40px 20px', background: 'rgba(255,255,255,0.3)', borderRadius: '24px', border: '1px solid rgba(255,255,255,0.4)', backdropFilter: 'blur(10px)' }}>
                    <Briefcase size={48} color="#111" style={{ margin: '0 auto 12px auto', display: 'block', opacity: 0.6 }} />
                    <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#111', marginBottom: '4px', letterSpacing: '-0.5px' }}>No Jobs Found</h3>
                    <p style={{ fontSize: '12px', fontWeight: '500', color: '#444' }}>No vacancies matching your search or filter.</p>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                    {filteredVacancies.map((job) => {
                         const displayFactoryName = (job.textile_name && job.textile_name.trim() !== '') 
                          ? job.textile_name 
                          : (job.factory_name && job.factory_name !== 'Textile Factory' ? job.factory_name : 'Textile Factory');                        const salaryText = job.salary ? `${job.salary} रुपये हफ्ते की सैलरी` : ' ओनर से बात कर के पता कर सकते हैं';                        const expVal = String(job.experience || '').trim().toLowerCase();
                        const displayExperience = (expVal === '' || expVal === '0' || expVal === 'fresher' || expVal === 'no') ? 'Fresher' : `${job.experience} Years`;    
                        const expVoiceMessage = displayExperience === 'Fresher' ? ' एक्सपीरियंस नहीं भी है तो चलेगा' : ` एक्सपीरियंस ${job.experience} साल का होना चाहिए`;
                        const descText = job.description ? ` description, ${job.description}` : '';
                        const exploreJobVoiceSummary = ` यह जॉब, ${displayFactoryName} कारखाने में, ${job.vacanciesCount}, ${job.role} की ज़रूरत हैं, इसके लिए ${salaryText} दी जाएंगी, और ${expVoiceMessage}, कारखाने का पता ${job.location || job.factory_address} है। ${descText} और जानकारी के लिए, आप कॉल बटन दबाकर सीधे कारखाने के ओनर से बात कर सकते हैं।`;
                        // 🌟 UPDATED: अब यह डेटाबेस से factory_name या textile_name को डायनामिकली लोड करेगा
                       
                        return (
                        <div 
                            key={job.id} 
                            style={{
                                background: 'rgba(255, 255, 255, 0.75)', 
                                backdropFilter: 'blur(24px) saturate(180%)',
                                WebkitBackdropFilter: 'blur(24px) saturate(180%)',
                                borderRadius: '28px', 
                                padding: '20px',
                                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.04), inset 0 1px 1px rgba(255,255,255,1)', 
                                border: '1px solid rgba(255, 255, 255, 0.6)',
                                marginBottom: '16px', 
                                position: 'relative',
                                overflow: 'hidden',
                                cursor: 'pointer',
                                transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
                            }}
                            onClick={(e) => {
                                if (!e.target.closest('button')) {
                                    handleVoiceClick && handleVoiceClick(e, exploreJobVoiceSummary, null, `card-explore-job-${job.id}`);
                                }
                            }}
                        >
                            {/* Ultra Luxury Smooth Vector Glow Gradients Inside Card */}
                            <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '140px', height: '140px', background: 'radial-gradient(circle, #38BDF8 0%, transparent 70%)', opacity: 0.12, filter: 'blur(24px)', pointerEvents: 'none' }} />
                            <div style={{ position: 'absolute', bottom: '-60px', left: '-60px', width: '140px', height: '140px', background: 'radial-gradient(circle, #34D399 0%, transparent 70%)', opacity: 0.08, filter: 'blur(24px)', pointerEvents: 'none' }} />

                            {/* Header Structure */}
                            <div style={{display:'flex', justifyContent:'space-between', alignItems:'flex-start', borderBottom:'1px solid rgba(15, 23, 42, 0.05)', paddingBottom:'14px', marginBottom:'14px', zIndex: 1, position: 'relative'}}>
                                <div>
                                    <span style={{fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '6px', display: 'block', letterSpacing:'0.8px'}}>Required Role</span>
                                    <div style={{ background: '#ffffff', border: '1px solid #E2E8F0', padding: '6px 14px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '6px', boxShadow: '0 2px 4px rgba(0,0,0,0.01)'}}>
                                        <span style={{fontSize: '16px', lineHeight: 1}}>💼</span>
                                        <h3 style={{color:'#0F172A', fontSize:'14px', fontWeight:'900', margin:0, letterSpacing: '-0.3px'}}>{job.role}</h3>
                                    </div>
                                    
                                    <div style={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '8px', marginTop: '4px'}}>
                                        <span className="force-grey" style={{fontSize: '10px', fontWeight: '900', color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px'}}>IN</span>
                                        <span style={{display:'flex', alignItems:'center', gap:'4px', padding: '4px 10px', borderRadius: '8px', border: '1px solid #E2E8F0', background: '#FFFFFF', fontSize:'11px', color:'#0F172A', fontWeight:'700', boxShadow: '0 2px 4px rgba(0,0,0,0.01)'}}>
                                            <Building size={12} color="#2563EB"/> {displayFactoryName}
                                        </span>
                                        <span style={{display:'flex', alignItems:'center', gap:'4px', padding: '4px 10px', background: '#FEF3C7', borderRadius: '8px', fontSize:'11px', color:'#B45309', fontWeight:'700'}}>
                                            <Users size={12} color="#D97706"/> {job.vacanciesCount} Openings
                                        </span>
                                    </div>
                                </div>
                                <span className="force-red" style={{padding:'5px 12px', borderRadius:'10px', fontSize:'10px', fontWeight:'900', background:'linear-gradient(135deg, #FEE2E2, #FCA5A5)', color:'#DC2626', display:'inline-flex', alignItems:'center', border:'none', letterSpacing:'0.8px'}}>
                                    URGENT
                                </span>
                            </div>

                            {/* FinTech Widgets Grid Exactly Matched with Owner Login Layout */}
                            <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px', marginBottom:'14px', zIndex: 1, position: 'relative'}}>
                                
                                {/* Widget 1: Salary */}
                                <div style={{ background: '#FFFFFF', border: '1px solid #F1F5F9', padding: '14px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.015)' }}>
                                    <div style={{ background: 'linear-gradient(135deg, #059669, #10B981)', padding: '8px', borderRadius: '12px', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(5, 150, 105, 0.2)' }}><IndianRupee size={16}/></div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{fontSize:'10px', color:'#64748B', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px'}}>Weekly Salary</span>
                                        <span style={{fontSize:'15px', fontWeight:'900', color: '#16A34A', letterSpacing: '-0.3px'}}>₹{job.salary}/wk</span>
                                    </div>
                                </div>

                                {/* Widget 2: Experience */}
                                <div style={{ background: '#FFFFFF', border: '1px solid #F1F5F9', padding: '14px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.015)' }}>
                                    <div style={{ background: 'linear-gradient(135deg, #0284C7, #38BDF8)', padding: '8px', borderRadius: '12px', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(2, 132, 199, 0.15)' }}><Briefcase size={16}/></div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{fontSize:'10px', color:'#64748B', fontWeight: '800', textTransform:'uppercase', letterSpacing:'0.5px'}}>Experience</span>
                                        <span style={{fontSize:'15px', fontWeight:'900', color: '#0F172A', letterSpacing: '-0.3px'}}>{displayExperience}</span>
                                    </div>
                                </div>

                                {/* Widget 3: Job Location (Full Width) */}
                                <div style={{ gridColumn: '1 / -1', background: '#FFFFFF', border: '1px solid #F1F5F9', padding: '14px', borderRadius: '18px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 4px 12px rgba(15, 23, 42, 0.015)' }}>
                                    <div style={{ background: 'linear-gradient(135deg, #D97706, #FBBF24)', padding: '8px', borderRadius: '12px', color: '#FFFFFF', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 10px rgba(217, 119, 6, 0.15)' }}><MapPin size={16}/></div>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{fontSize:'10px', color:'#64748B', fontWeight:'800', textTransform:'uppercase', letterSpacing:'0.5px'}}>Job Location</span>
                                        <span style={{fontSize:'13px', fontWeight:'800', color: '#334155', marginTop:'2px', lineHeight: '1.4'}}>{job.location || job.factory_address}</span>
                                    </div>
                                </div>
                            </div>

                            <span style={{fontSize: '10px', fontWeight: '800', color: '#64748B', textTransform: 'uppercase', marginBottom: '6px', display: 'block', letterSpacing:'0.5px', paddingLeft: '4px'}}>Description</span>
                            <div style={{background:'rgba(15, 23, 42, 0.02)', padding:'12px 16px', borderRadius:'16px', border:'1px solid rgba(15, 23, 42, 0.03)', marginBottom:'14px'}}>
                                <p style={{fontSize:'13px', color:'#475569', margin:0, lineHeight:'1.6', fontWeight:'700'}}>{job.description || "Owner se baat karke details lein."}</p>
                            </div>

                            {/* Bottom Strip Actions */}
                            <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', paddingTop:'14px', borderTop:'1px solid rgba(15, 23, 42, 0.05)', zIndex: 1, position: 'relative'}}>
                                <span className="force-grey" style={{fontSize:'12px', fontWeight:'700', color:'#64748B', display:'flex', alignItems:'center', gap:'6px'}}><Clock size={12} color="#94A3B8"/> Posted: {job.dateAdded}</span>
                                <button 
                                    onClick={(e) => handleVoiceClick && handleVoiceClick(e, " इस कॉल बटन से आप डायरेक्ट टेक्सटाइल कारखाने के मालिक को जॉब के लिए phone लगा सकते हैं।", () => window.location.href = `tel:${job.contactNumber}`, `call-${job.id}`)} 
                                    style={{ 
                                        background: '#0F172A', color: '#FFFFFF', border: 'none', padding: '10px 20px', borderRadius: '14px', fontSize: '13px', fontWeight: '900', gap: '6px', minHeight: 'auto', boxShadow: '0 8px 20px rgba(15, 23, 42, 0.16)', transition: 'all 0.2s', display: 'flex', alignItems: 'center' 
                                    }}
                                >
                                    <PhoneCall size={14} className="force-white" strokeWidth={2.5} /> <span className="force-white" style={{fontWeight:'900'}}>Call Now</span>
                                </button>
                            </div>
                        </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

const styles = {
    content: { 
        padding: '16px', maxWidth: '600px', margin: '0 auto', paddingBottom: '30px', minHeight: '100vh', boxSizing: 'border-box',
        WebkitOverflowScrolling: 'touch',
        overscrollBehaviorY: 'contain'
    }
};